
import React, { useMemo } from 'react';
import { Accordion } from "@/components/ui/accordion";
import { Calendar, MapPin, Users, Check } from "lucide-react";
import { Event } from "@/types/event";
import { AccordionSection } from "@/components/create-event/form/sections/AccordionSection";
import { EventDateTime } from '../EventDateTime';
import { EventLocation } from '../EventLocation';
import { InvitedGuestList } from '../InvitedGuestList';
import { EventDateTimeTable } from '../EventDateTimeTable';
import { GuestFeedbackDateTimeTable } from '../guest-list/GuestFeedbackDateTimeTable';
import { useEventAvailability } from '@/hooks/use-event-availability';
import { useEventGuests } from '@/hooks/use-event-guests';
import { RsvpAccordionContent } from '../guest-view/RsvpAccordionContent';
import { RsvpStatusBadge } from '../guest-view/components/RsvpStatusBadge';
import { useRsvpUserData } from '@/hooks/use-rsvp-user-data';

interface EventDetailsAccordionProps {
  event: Event;
  isHost: boolean;
  onRemoveGuest?: (userId: string) => Promise<void>;
  onEdit?: () => void;
  rsvpStatus?: 'invited' | 'accepted' | 'declined' | 'maybe';
}

export const EventDetailsAccordion = ({
  event,
  isHost,
  onRemoveGuest,
  onEdit,
  rsvpStatus
}: EventDetailsAccordionProps) => {
  // All sections are considered valid in view mode
  const isValid = true;

  // Get availability data
  const {
    data: availabilityData = {}
  } = useEventAvailability(event.event_id);

  // Get guest info for the availability modal
  const {
    invites
  } = useEventGuests(event.event_id);

  // Get current user's RSVP data if not a host
  const { selectedDates, selectedTimes } = useRsvpUserData(event.event_id, !isHost);

  // Create a mapping of invite_id to guest info for the modal
  const guestInfoMap = useMemo(() => {
    const map: Record<string, any> = {};
    invites.forEach(invite => {
      map[invite.invite_id] = {
        invite_id: invite.invite_id,
        display_name: invite.display_name,
        avatar_url: invite.avatar_url,
        user_full_name: invite.user_full_name,
        guest_avatar_url: invite.guest_avatar_url,
        selected_times: invite.selected_times // Include selected_times
      };
    });
    return map;
  }, [invites]);

  // Enhance availability data with guest info
  const enhancedAvailabilityData = useMemo(() => {
    const result: Record<string, Record<string, any[]>> = {};
    Object.entries(availabilityData).forEach(([date, timeSlots]) => {
      result[date] = {};
      Object.entries(timeSlots).forEach(([timeSlot, inviteIds]) => {
        // Make sure inviteIds is always an array
        const idsArray = Array.isArray(inviteIds) ? inviteIds : [];
        result[date][timeSlot] = idsArray.map(id => {
          // Look up the guest info by invite_id or create a default if not found
          return guestInfoMap[id] || {
            invite_id: id,
            display_name: typeof id === 'string' ? `Guest ${id.substring(0, 5)}` : `Guest`
          };
        });
      });
    });
    return result;
  }, [availabilityData, guestInfoMap]);

  // Determine which sections should be open by default
  const defaultOpenSections = useMemo(() => {
    const sections = [];
    // For confirmed events, open all sections by default
    if (event.status === 'confirmed' || event.status === 'booked') {
      sections.push('when', 'where', 'who');
    }
    // For guest view with RSVP status, open the RSVP section
    if (!isHost && rsvpStatus) {
      sections.push('your-rsvp');
    }
    return sections;
  }, [isHost, rsvpStatus, event.status]);

  // Check if the event is confirmed/booked for consistent rendering between host and guest views
  const isConfirmedOrBooked = event.status === 'confirmed' || event.status === 'booked';

  return (
    <Accordion 
      type="multiple" 
      className="space-y-4 mt-6" 
      defaultValue={defaultOpenSections}
    >
      {!isHost && rsvpStatus && (
        <AccordionSection
          value="your-rsvp"
          title="Your RSVP"
          icon={Check}
          isValid={isValid}
          useDetailsHeader={true}
          extraContent={rsvpStatus && <RsvpStatusBadge status={rsvpStatus} />}
        >
          <RsvpAccordionContent 
            eventId={event.event_id} 
            currentStatus={rsvpStatus} 
          />
        </AccordionSection>
      )}

      <AccordionSection 
        value="when" 
        title="When" 
        icon={Calendar} 
        isValid={isValid} 
        useDetailsHeader={true}
      >
        <div className="py-0 px-0">
          {isConfirmedOrBooked ? (
            <EventDateTime 
              status={event.status} 
              eventDate={event.event_date} 
              preferredDates={event.preferred_dates} 
              selectedDate={event.selectedDate} 
              preferredTimeOfDay={event.preferred_time_of_day}
              specificTimePreference={event.specific_time_preference}
            />
          ) : (
            event.status === 'planned' ? (
              isHost ? (
                <EventDateTimeTable 
                  eventDates={event.preferred_dates || []} 
                  preferredTimeOfDay={event.preferred_time_of_day || []} 
                  selectedDate={event.selectedDate} 
                  guestAvailability={enhancedAvailabilityData} 
                />
              ) : (
                <GuestFeedbackDateTimeTable 
                  allDates={event.preferred_dates || []}
                  selectedDates={selectedDates}
                  selectedTimes={selectedTimes}
                />
              )
            ) : (
              <EventDateTime 
                status={event.status} 
                eventDate={event.event_date} 
                preferredDates={event.preferred_dates} 
                selectedDate={event.selectedDate} 
                preferredTimeOfDay={event.preferred_time_of_day}
                specificTimePreference={event.specific_time_preference}
              />
            )
          )}
        </div>
      </AccordionSection>

      <AccordionSection 
        value="where" 
        title="Where" 
        icon={MapPin} 
        isValid={isValid} 
        useDetailsHeader={true}
      >
        <div className="px-0 py-0">
          <EventLocation 
            status={event.status} 
            locationType={event.location_type} 
            flexibleLocationDescription={event.flexible_location_description || ''} 
            preferredVenues={event.preferred_venues} 
            selectedVenue={event.selectedVenue} 
            eventId={event.event_id} 
            isHost={isHost} 
          />
        </div>
      </AccordionSection>

      <AccordionSection 
        value="who" 
        title="Who" 
        icon={Users} 
        isValid={isValid} 
        useDetailsHeader={true}
      >
        <div className="px-0 py-0">
          <InvitedGuestList 
            eventId={event.event_id} 
            isHost={isHost} 
            onRemoveGuest={onRemoveGuest} 
            status={event.status}
          />
        </div>
      </AccordionSection>
    </Accordion>
  );
};
